import mixpanel from "mixpanel-browser"

interface InitOverrides {
  userId?: string | number
  userType?: "user" | "client"
}

class EventAnalytics {
  private initialized = false

  init(overrides?: InitOverrides): void {
    try {
      if (this.initialized) return
      if (window.FinancialCents?.impersonating) return

      const token = window.keys?.mixpanel
      if (!token) {
        console.warn("EventAnalytics: Mixpanel token not found.")
        return
      }

      mixpanel.init(token, {
        persistence: "sessionStorage" as "localStorage",
        track_pageview: "url-with-path",
      })

      const user = window.FinancialCents?.user || window.FinancialCents?.contact
      const company = window.FinancialCents?.company

      const distinctId = overrides?.userId ?? user?.id
      const userType = overrides?.userType ?? (window.FinancialCents?.contact ? "client" : "user")

      if (distinctId) {
        mixpanel.identify(String(distinctId))

        if (user?.id) {
          mixpanel.people.set({
            user_id: user.id,
            $name: user.name,
            $email: user.email,
            user_type: userType,
          })

          if (company?.id) {
            mixpanel.people.union({ company_id: company.id })
            mixpanel.set_group("company_id", company.id)
          }
        }
      }

      this.initialized = true
    } catch (e) {
      console.error("EventAnalytics: Failed to initialize Mixpanel.", e)
    }
  }

  reset(): void {
    try {
      mixpanel.reset()
    } catch (e) {
      console.error("EventAnalytics: Failed to reset Mixpanel.", e)
    }
  }

  track(event: string, properties?: Record<string, unknown>): void {
    try {
      if (!this.initialized) return

      mixpanel.track(event, {
        company_id: window.FinancialCents?.company?.id ?? null,
        subscription: window.FinancialCents?.company?.plan ?? null,
        ...properties,
      })
    } catch (e) {
      console.error("EventAnalytics: Failed to track event.", e)
    }
  }

  registerSuperProperty(key: string, value: unknown): void {
    try {
      if (!this.initialized) return
      mixpanel.register({ [key]: value })
    } catch (e) {
      console.error("EventAnalytics: Failed to register super property.", e)
    }
  }
}

export default new EventAnalytics()
